// src/pages/Dashboard/components/MorningRoutine.jsx
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  The one rep follows this morning's meeting. When the manager
//               has named a step as the skill focus, every rep's sixty seconds
//               is on that step, which is how one focus reaches the whole
//               floor rather than only the people listening at 8:30.
//   2026-09-27  New. Replaces DailyCheckIn as the first thing a salesperson
//               sees. The check-in is step one of three rather than the whole
//               moment. Logic lives in src/lib/morningRoutine.js.
//
// TWO MINUTES, THEN GO SELL.
//
// See the header of src/lib/morningRoutine.js for why these three and in this
// order. This file is the screen.
//
// IT IS A SEQUENCE, SO IT IS NUMBERED. The house rule is that numbers on a
// page have to mean something, and here they do: you answer yesterday before
// you look at today, and you warm up last because it is the thing closest to
// the first customer.
//
// ONE STEP IS OPEN AT A TIME. The finished ones collapse to a green tick and a
// few words, the upcoming ones sit quiet, and the current one is the only thing
// asking for anything. Three open forms on a phone is a form. One question at
// a time is a conversation, which is the standard Jazz holds a salesperson to
// in an interview and the one this screen should hold itself to.
//
// DONE COLLAPSES TO ONE LINE. Once it is finished the routine gets out of the
// way, because the whole point is that they close the app and go to the floor.
//
// STATE IS PER VIEWER, PER DAY, IN LOCALSTORAGE, and wrapped because storage can
// throw in a private window. It is a convenience: losing it means answering
// three questions again, not losing anything that matters. The fact that the
// routine happened is written to activity_events once, on completion, so a
// manager can see who warmed up before the shift. activity_self_insert allows
// it and activity_manager_read lets their manager read it.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Flex, HStack, VStack, Text, Button, Icon } from '@chakra-ui/react'
import { Check, Phone, Dumbbell, RotateCcw } from 'lucide-react'
import { supabase } from '../../../lib/supabase'
import { resolveCommitment } from '../../../lib/progressStore'
import {
  UNSOLD_CHOICES, unsoldLabel, routineKey, emptyRoutine, parseRoutine,
  doneSteps, currentStep, isComplete, followupLink, repLink, finishedLine,
} from '../../../lib/morningRoutine.js'

function load(key) {
  try {
    return parseRoutine(localStorage.getItem(key))
  } catch {
    return emptyRoutine()
  }
}
function save(key, state) {
  try {
    localStorage.setItem(key, JSON.stringify(state))
  } catch {
    /* private window, full storage: the routine still works for this visit */
  }
}

// The marker down the left of each step. Green with a tick when done, which is
// the done state Tyler asked for across the product; an ink ring when it is the
// step being asked; a hairline when it is still to come.
function Marker({ n, state }) {
  if (state === 'done') {
    return (
      <Flex boxSize="26px" borderRadius="full" bg="success" color="onSuccess" align="center" justify="center" flexShrink={0}>
        <Icon as={Check} boxSize={3.5} strokeWidth={3} />
      </Flex>
    )
  }
  return (
    <Flex
      boxSize="26px"
      borderRadius="full"
      border={state === 'current' ? '2px solid' : '1px solid'}
      borderColor={state === 'current' ? 'ink' : 'line'}
      color={state === 'current' ? 'ink' : 'inkDim'}
      align="center"
      justify="center"
      flexShrink={0}
      fontSize="12px"
      fontWeight={600}
      fontFamily="mono"
    >
      {n}
    </Flex>
  )
}

function Step({ n, title, summary, state, children }) {
  return (
    <HStack align="flex-start" spacing={4} py={4} borderTop={n === 1 ? 'none' : '1px solid'} borderColor="line">
      <Marker n={n} state={state} />
      <Box flex={1} minW={0} pt="2px">
        <Text
          fontWeight={600}
          letterSpacing="-0.01em"
          color={state === 'upcoming' ? 'inkDim' : 'ink'}
          mb={state === 'current' ? 2 : 0}
        >
          {title}
        </Text>
        {state === 'done' && summary && (
          <Text fontSize="body-sm" color="inkMuted" mt={0.5}>
            {summary}
          </Text>
        )}
        {state === 'current' && children}
      </Box>
    </HStack>
  )
}

export default function MorningRoutine({ userId, commitment, currentStepInfo, focus, onCommitmentResolved }) {
  // The meeting's step wins over the person's own current step. Somebody on
  // Step 3 practising the store's Step 8 focus is doing exactly what the
  // manager asked, and it is sixty seconds, not a change to their course.
  const repStep = focus?.number ? focus : currentStepInfo
  const navigate = useNavigate()
  const key = useMemo(() => routineKey(userId), [userId])
  const [state, setState] = useState(() => load(key))
  const hasCommitment = Boolean(commitment)
  const opts = { hasCommitment }

  // A new day while the tab stayed open, or a different person on a shared
  // phone, gets a fresh routine rather than somebody else's morning.
  useEffect(() => {
    setState(load(key))
  }, [key])

  const update = (patch) => {
    setState((prev) => {
      const next = { ...prev, ...patch }
      if (!prev.finishedAt && isComplete(next, opts)) next.finishedAt = new Date().toISOString()
      save(key, next)
      return next
    })
  }

  // Record the routine once, the moment it completes. Fire and forget from the
  // browser is fine here, unlike in a Lambda: the page stays alive, and a
  // missed row costs a manager one tick on a board rather than anybody's work.
  useEffect(() => {
    if (!state.finishedAt || !userId) return
    const sentKey = `${key}.sent`
    try {
      if (localStorage.getItem(sentKey)) return
      localStorage.setItem(sentKey, '1')
    } catch {
      /* without storage we may record twice, which is harmless */
    }
    supabase
      .from('activity_events')
      .insert({
        user_id: userId,
        kind: 'morning_routine',
        metadata: { unsold: state.unsold, kept: state.yesterday === 'kept' ? true : state.yesterday === 'missed' ? false : null },
      })
      .then(({ error }) => {
        if (error) console.error('morning routine: event not recorded', error.message)
      })
  }, [state.finishedAt, userId, key, state.unsold, state.yesterday])

  const done = doneSteps(state, opts)
  const now = currentStep(state, opts)
  const stateOf = (s) => (done[s] ? 'done' : now === s ? 'current' : 'upcoming')

  // ------------------------------------------------------------- finished
  if (isComplete(state, opts)) {
    const at = state.finishedAt
      ? new Date(state.finishedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      : null
    return (
      <HStack bg="card" border="1px solid" borderColor="line" borderRadius="card" px={5} py={4} spacing={4}>
        <Flex boxSize="30px" borderRadius="full" bg="success" color="onSuccess" align="center" justify="center" flexShrink={0}>
          <Icon as={Check} boxSize={4} strokeWidth={3} />
        </Flex>
        <Box flex={1} minW={0}>
          <Text fontWeight={600} letterSpacing="-0.01em">
            Warmed up{at ? ` at ${at}` : ''}. Go sell.
          </Text>
          <Text fontSize="body-sm" color="inkMuted" noOfLines={1}>
            {finishedLine(state)}
          </Text>
        </Box>
        {/* Somebody who tapped a number by mistake needs a way back that is
            not clearing their browser. Quiet, because it is the exception. */}
        <Button
          size="sm"
          variant="ghost"
          color="inkDim"
          leftIcon={<RotateCcw size={14} />}
          onClick={() => {
            const fresh = { ...emptyRoutine(), yesterday: state.yesterday }
            save(key, fresh)
            setState(fresh)
          }}
          aria-label="Redo this morning's routine"
        >
          Redo
        </Button>
      </HStack>
    )
  }

  // ------------------------------------------------------------- in progress
  return (
    <Box bg="card" border="1px solid" borderColor="lineStrong" borderRadius="cardLg" px={{ base: 5, md: 6 }} pt={5} pb={2}>
      <Text textStyle="eyebrow" color="accent.500" mb={1}>
        Before your first up
      </Text>
      <Text fontSize={{ base: 'lead', md: '22px' }} fontWeight={600} letterSpacing="-0.02em" mb={1}>
        Two minutes, then go sell.
      </Text>

      <VStack align="stretch" spacing={0} mt={2}>
        {/* 1. YESTERDAY */}
        {/* THE ANSWER WINS OVER THE MISSING COMMITMENT. Answering refreshes
            Today, the commitment resolves, and this component then receives
            no commitment at all. Checked in the other order, the step read
            "nothing to check" a moment after somebody had checked it. */}
        <Step
          n={1}
          title={state.yesterday || hasCommitment ? 'Yesterday' : 'Yesterday: nothing to check'}
          state={stateOf('yesterday')}
          summary={
            state.yesterday === 'kept'
              ? 'You did it. That is how the process sticks.'
              : state.yesterday === 'missed'
                ? 'Not yet. Run it once with the coach before the floor gets busy.'
                : 'Write a commitment after your next step and it shows up here tomorrow.'
          }
        >
          <Text fontSize="body-sm" color="inkMuted" mb={1}>
            You committed to this on your next customer:
          </Text>
          <Text fontSize="body" fontStyle="italic" color="ink" lineHeight={1.55} mb={3}>
            &ldquo;{commitment?.body}&rdquo;
          </Text>
          <HStack spacing={2}>
            <Button
              size="sm"
              minH="40px"
              onClick={() => {
                resolveCommitment(userId, commitment.id, 'kept')
                update({ yesterday: 'kept' })
                onCommitmentResolved?.()
              }}
            >
              I did it
            </Button>
            <Button
              size="sm"
              minH="40px"
              variant="outline"
              onClick={() => {
                resolveCommitment(userId, commitment.id, 'missed')
                update({ yesterday: 'missed' })
                onCommitmentResolved?.()
              }}
            >
              Not yet
            </Button>
          </HStack>
        </Step>

        {/* 2. YOUR UNSOLD */}
        <Step
          n={2}
          title="Your unsold"
          state={stateOf('unsold')}
          summary={
            state.unsold === 0
              ? 'Nobody walked. Clean day.'
              : `${unsoldLabel(state.unsold ?? 0)} walked. Practise the callback before you make it.`
          }
        >
          <Text fontSize="body-sm" color="inkMuted" mb={3}>
            How many walked yesterday without buying?
          </Text>
          {/* ONE ROW, NEVER WRAPPED. At 375px six chips at 46px wrapped and left
              "5+" alone on a second line, which reads as an afterthought and is
              exactly the stacking Tyler asked to avoid. 40px wide keeps all six
              on one line inside the card; the height stays at 44 because that
              is the dimension a thumb actually misses on. */}
          <HStack spacing={1.5} role="group" aria-label="How many walked without buying">
            {UNSOLD_CHOICES.map((n) => (
              <Button
                key={n}
                size="sm"
                minW="40px"
                px={0}
                h="44px"
                flex="1 1 0"
                maxW="52px"
                variant="outline"
                borderColor="lineStrong"
                fontFamily="mono"
                onClick={() => update({ unsold: n })}
                aria-label={n >= 5 ? 'Five or more' : `${n}`}
              >
                {unsoldLabel(n)}
              </Button>
            ))}
          </HStack>
        </Step>

        {/* The callback, offered the moment there is somebody to call, and
            outside the step so it stays reachable after the count is in. */}
        {state.unsold > 0 && !done.rep && (
          <HStack pl="42px" pb={4} spacing={3} flexWrap="wrap" rowGap={2}>
            <Button
              size="sm"
              minH="40px"
              leftIcon={<Phone size={15} />}
              onClick={() => navigate(followupLink(state.unsold))}
            >
              Practise one callback
            </Button>
            <Text fontSize="xs" color="inkDim" lineHeight={1.4}>
              Describe the situation, not the person.
            </Text>
          </HStack>
        )}

        {/* 3. ONE REP */}
        <Step
          n={3}
          title="One rep"
          state={stateOf('rep')}
          summary="In the cage before the game."
        >
          {focus?.number && (
            <Text fontSize="xs" color="accent.500" fontFamily="mono" textTransform="uppercase" letterSpacing="0.06em" mb={1}>
              This morning's focus
            </Text>
          )}
          <Text fontSize="body-sm" color="inkMuted" mb={3}>
            {repStep
              ? `Sixty seconds on Step ${repStep.number}, ${repStep.title}. The coach plays the customer.`
              : 'Sixty seconds on any step. The coach plays the customer.'}
          </Text>
          <Button
            size="sm"
            minH="40px"
            leftIcon={<Dumbbell size={15} />}
            onClick={() => {
              update({ rep: true })
              navigate(repLink(repStep?.number))
            }}
          >
            Run it
          </Button>
        </Step>
      </VStack>
    </Box>
  )
}
